// src/hooks/usePostSignupUTM.ts
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Convert a snake_case string to camelCase.
 * Example: "utm_source" → "utmSource"
 */
function toCamelCase(s: string): string {
  return s.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Given an object whose keys are snake_case, return a new object
 * where each key is converted to camelCase. Values are preserved.
 */
function convertKeysToCamelCase(
  snakeObj: Record<string, string>
): Record<string, string> {
  const camelObj: Record<string, string> = {};
  Object.entries(snakeObj).forEach(([key, value]) => {
    const camelKey = toCamelCase(key);
    camelObj[camelKey] = value;
  });
  return camelObj;
}

export const usePostSignupUTM = () => {
  const [modalContent, setModalContent] = useState<{
    htmlContent: string;
    utmId: string;
  } | null>(null);

  const location = useLocation();

  useEffect(() => {
    const checkPostSignup = async () => {
      // 1️⃣ Check if user is logged in
      const token = localStorage.getItem("token");
      if (!token) return;

      const trackedKey = "utm_pre_signup_tracked";

      // 1️⃣ If we've already set this flag, do nothing:
      const hasBeenTracked = localStorage.getItem(trackedKey);
      if (hasBeenTracked == "true") return;

      // 3️⃣ Get UTM params
      const storedUTM = localStorage.getItem("utm_params");
      if (!storedUTM) return;

      try {
        const utmParamsSnake = JSON.parse(storedUTM);
        const utmParams = convertKeysToCamelCase(utmParamsSnake);

        // 4️⃣ Fetch UTM config
        const response = await axios.get<{ utms: Array<any> }>(
          `${process.env.REACT_APP_REST_API_URI}/api/v1/shown/all-registered`
        );

        const registeredUTMs = response.data.utms;

        if (!Array.isArray(registeredUTMs)) return;

        // 5️⃣ Find matching UTM configuration
        const utmKeys = [
          "utmMedium",
          "utmCampaign",
          "utmSource",
          "utmTerm",
          "utmContent",
        ];
        const filteredUtmParams = Object.entries(utmParams).filter(([key]) =>
          utmKeys.includes(key)
        );

        // Then use filteredUtmParams in the matching logic
        const matched = registeredUTMs.find((utmConfig) =>
          filteredUtmParams.every(([key, value]) => utmConfig[key] == value)
        );

        if (matched?.postSignups?.length > 0) {
          // 6️⃣ Process popup text with label replacement
          let processedHtml = matched.popupText;
          const labelMap: Record<string, string> = {};

          // Create label to prompt mapping
          matched.postSignups.forEach((parm: any) => {
            const { label, prompt } = parm;
            labelMap[label] = prompt;
          });

          // Replace labels with anchor tags
          // Updated portion of the usePostSignupUTM hook
          processedHtml = processedHtml
            .split("\n")
            .map((line: any) => {
              const trimmedLine = line.trim();
              const postSignup = matched.postSignups.find(
                (ps: any) => ps.label === trimmedLine
              );

              if (postSignup) {
                const encodedPrompt = encodeURIComponent(postSignup.prompt);
                return `<a href="/chat/${
                  matched.character
                }?prompt=${encodedPrompt}${
                  utmParams.bookTalk ? `&bookTalk=${utmParams.bookTalk}` : ""
                }&send=true" 
       class="text-blue-600 hover:underline transition-colors">
       ${trimmedLine}
     </a>`;
              }
              return line; // Return original line if no match
            })
            .join("<br/>"); // Use HTML line breaks instead of \n

          setModalContent({
            htmlContent: processedHtml,
            utmId: matched._id,
          });
          localStorage.setItem(trackedKey, "true");
        }
      } catch (error) {
        console.error("Post-signup UTM error:", error);
      }
    };

    setTimeout(checkPostSignup, 500);
  }, [[location.pathname, location.search]]);

  return modalContent;
};
